// src/components/Dashboard.js
import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  useUser,
  UserButton,
  useOrganization,
  useOrganizationList,
  OrganizationSwitcher,
} from "@clerk/clerk-react";
import {
  Home,
  BookOpen,
  Settings,
  MessageCircle,
  Contact,
  BarChart3,
  Menu,
  X,
  Award,
  Building2,
  Users,
  CreditCard,
  Lock,
  Copy,
  FileText,
  MessageSquare
} from "lucide-react";
import toast from "react-hot-toast";
// Import context
import {
  useOrganizationContext, CreateFirstOrganization,
  SelectOrganization,
} from "../contexts/OrganizationContext";

// Import your custom create-org form
import { CustomCreateOrganization } from "./CustomCreateOrganization";

// Import tab components
import Overview from "./tabs/Overview";
import AboutSection from "./tabs/AboutSection";
import KnowledgeVault from "./tabs/KnowledgeVault";
import WidgetManager from "./tabs/WidgetManager";
import AnswerOptimization from "./tabs/AnswerOptimization";
import SupportQueries from "./tabs/SupportQueries";
import ApiKeys from "./tabs/ApiKeys";
import ApiGuide from "./tabs/ApiGuide";
import AITrafficAnalytics from "./tabs/AITrafficAnalytics";
import Billing from "./tabs/Billing";
import Invoices from "./tabs/Invoices";
import IntegrationsLanding from "./tabs/IntegrationsLanding";
import Integrations from "./tabs/Integrations";
import IntegrationDetails from "./tabs/IntegrationDetails";
import WooCommerceIntegrations from "./tabs/WooCommerceIntegrations";
import WooCommerceIntegrationDetails from "./tabs/WooCommerceIntegrationDetails";
import ConversationsPage from "./tabs/ConversationsPage";
import NotFound from "./NotFound";

import useBillingStore from "../store/useBillingStore";
import ApiService from "../services/ApiService";
import Reports from "./tabs/Reports";
import FAQManagement from "./tabs/FAQManagement";
import CSATScores from "./tabs/CSATScores";
import SupportInbox from "./tabs/SupportInbox";
import UsageCard from "./ui/UsageCard";

const navigation = [
  { name: "Overview", href: "/", icon: Home },
  { name: "Settings", href: "/about", icon: Building2 },
  { name: "Knowledge Vault", href: "/knowledge", icon: BookOpen },
  { name: "FAQ Management", href: "/faqs", icon: FileText },
  { name: "Widget Manager", href: "/widgets", icon: Settings },
  { name: "Content Optimization", href: "/optimization", icon: MessageCircle },
  { name: "Support Queries", href: "/support", icon: Contact },
  { name: "Support Inbox", href: "/support-inbox", icon: MessageCircle },
  { name: "Conversations", href: "/conversations", icon: MessageSquare },
  { name: "CSAT Scores", href: "/csat", icon: Award },
  { name: "API Keys", href: "/api-keys", icon: Lock },
  { name: "Reports", href: "/reports", icon: BarChart3 },
  { name: "Integrations", href: "/integrations", icon: Users },
  { name: "Billing", href: "/billing", icon: CreditCard },
  // { name: "Invoices", href: "/invoices", icon: CreditCard },
];




// const navigation = [
//   { name: "Overview", href: "/", icon: Home },
//   { name: "About", href: "/about", icon: Building2 },
//   { name: "Knowledge Vault", href: "/knowledge", icon: BookOpen },
//   { name: "Widget Manager", href: "/widgets", icon: Settings },
//   { name: "Answer Optimization", href: "/optimization", icon: MessageCircle },
//   { name: "Support Queries", href: "/support", icon: LifeBuoy },
//   { name: "AI Traffic Analytics", href: "/analytics", icon: BarChart3 },
// ];

function Dashboard() {
  return <DashboardContent />;
}

function DashboardContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { organization, isLoaded } = useOrganization();
  const { organizationList } = useOrganizationList();
  const { orgReady, isInitializing } = useOrganizationContext();
  const { fetchBillingInfo } = useBillingStore();

  console.log('here is the user details', user);

  useEffect(() => {
    if (organization && organization.id) {
      localStorage.setItem('isOrgExist', 'yes')
    } else {
      localStorage.setItem('isOrgExist', 'no')
    }
  }, [organization])

  // Handle redirect after sign in (set from LandingPage before opening Clerk modal)
  useEffect(() => {
    try {
      const redirectAfter = localStorage.getItem("redirectAfterSignIn");
      if (redirectAfter) {
        console.log(`[Dashboard] Found redirectAfterSignIn: ${redirectAfter}. Redirecting...`);
        localStorage.removeItem("redirectAfterSignIn");
        navigate(redirectAfter, { replace: true });
      }
    } catch (err) {
      console.warn("redirectAfterSignIn check failed in Dashboard", err);
    }
  }, [navigate]);

  // Show first-time plan selection prompt on Overview once per organization
  useEffect(() => {
    if (!orgReady || !organization) return;
    // Only on Overview route
    if (location.pathname !== "/") return;

    const key = `planPromptShown_${organization.id}`;
    if (localStorage.getItem(key) === "1") return;

    const tId = toast((t) => (
      <div className="flex items-start gap-3">
        <div className="text-sm">
          <div className="font-semibold text-gray-900">Welcome! Finish setup</div>
          <div className="text-gray-700">Choose a plan to activate your free trial.</div>
        </div>
        <button
          onClick={() => {
            localStorage.setItem(key, "1");
            toast.dismiss(t.id);
            navigate("/billing");
          }}
          className="ml-4 px-3 py-1.5 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-800"
        >
          Choose Plan
        </button>
      </div>
    ), { duration: 8000 });

    // Mark as shown so it doesn't reappear on refresh
    localStorage.setItem(key, "1");

    return () => {
      if (tId) toast.dismiss(tId);
    };
  }, [orgReady, organization, location.pathname, navigate]);

  useEffect(() => {
    const checkHealth = async () => {
      if (!orgReady) return;
      try {
        const health = await ApiService.healthCheck();
        if (health.status !== "healthy") {
          toast.error("Some services are unavailable");
        }
      } catch (error) {
        console.error("Health check failed:", error);
        toast.error("Unable to connect to backend services");
      }
    };
    checkHealth();
  }, [orgReady]);

  useEffect(() => {
    if (orgReady) {
      fetchBillingInfo();
    }
  }, [orgReady, fetchBillingInfo]);

  const urlParams = new URLSearchParams(window.location.search);
  const isAutomated = urlParams.get('automated') === 'true';

  if (isInitializing || !isLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (organizationList?.length === 0 && !isAutomated) {
    return <CreateFirstOrganization />;
  }

  if (!organization && !isAutomated) {
    return <SelectOrganization />;
  }

  if (!orgReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto" />
          <p className="mt-4 text-gray-600">Setting up organization...</p>
        </div>
      </div>
    );
  }

  return <MainDashboard />;
}

function MainDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key
  const { user } = useUser();
  const [isSuspended, setIsSuspended] = useState(false);
  const { organization } = useOrganizationContext();

  useEffect(() => {
    async function checkSuspension() {
      try {
        const data = await ApiService.getOrganizationData();
        if (data && data.status === "suspended") {
          setIsSuspended(true);
        } else {
          setIsSuspended(false);
        }
      } catch (err) {
        console.error("Failed to fetch org status:", err);
      }
    }
    
    if (organization?.id) {
      checkSuspension();
      // Poll every 10 seconds to instantly detect if the org gets suspended
      const intervalId = setInterval(checkSuspension, 10000);
      return () => clearInterval(intervalId);
    }
  }, [organization?.id, refreshKey]);

  // Open modal when hash is "#create-org"
  useEffect(() => {
    setCreateOrgOpen(location.hash === "#create-org");
  }, [location.hash]);
  const closeCreateOrg = () => {
    // remove hash without pushing history
    navigate(location.pathname, { replace: true });
    // Trigger a refresh of child components
    setRefreshKey((prev) => prev + 1);
  };

  // Validate routes
  const validRoutes = [
    "/",
    "/about",
    "/knowledge",
    "/faqs",
    "/widgets",
    "/optimization",
    "/support",
    "/support-inbox",
    "/conversations",
    "/csat",
    "/api-keys",
    "/api-guide",
    "/analytics",
    "/reports",
    "/billing",
    "/invoices",
    "/integrations",
  ];

  // Allow dynamic routes for support tickets and knowledge vault items
  const isSupportTicketRoute = location.pathname.startsWith("/support/") && location.pathname.length > "/support/".length;
  const isKnowledgeVaultRoute = location.pathname.startsWith("/knowledge/") && location.pathname.length > "/knowledge/".length;
  const isIntegrationRoute = location.pathname.startsWith("/integrations/") && location.pathname.length > "/integrations/".length;

  if (!validRoutes.includes(location.pathname) && !isSupportTicketRoute && !isKnowledgeVaultRoute && !isIntegrationRoute) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white shadow-2xl">
            <button
              className="absolute top-0 right-0 m-4 z-10"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-gray-700" />
            </button>
            <div className="flex-1 overflow-y-auto">
              <SidebarContent setSidebarOpen={setSidebarOpen} isSuspended={isSuspended} />
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 z-30">
        <div className="flex-1 flex flex-col bg-white border-r border-gray-200 shadow-sm overflow-y-auto">
          <SidebarContent setSidebarOpen={() => { }} isSuspended={isSuspended} />
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top nav */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Mobile menu */}
              <div className="md:hidden">
                <button
                  className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
              </div>

              {/* Org switcher */}
              <div className="flex-1 flex items-center">
                <OrganizationSwitcher
                  appearance={{
                    elements: {
                      organizationSwitcherTrigger:
                        "flex items-center space-x-2 px-4 py-2.5 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer shadow-sm",

                      organizationSwitcherPopoverCard:
                        "bg-white border border-gray-200 rounded-lg shadow-xl mt-2",

                      organizationSwitcherPopoverActionButton:
                        "w-full text-left px-3 py-1 text-sm hover:bg-gray-50 rounded-md",

                      /* ✅ Avatar cleanup */
                      organizationPreviewAvatarBox:
                        "bg-black before:content-['🏢'] before:text-white before:text-sm text-gray-700 rounded-md w-7 h-7 flex items-center justify-center text-xs font-semibold",

                      organizationPreviewAvatarImage:
                        "object-cover bg-black hidden",

                      organizationSwitcherTriggerIcon:
                        "text-gray-600",
                    },
                  }}
                  createOrganizationMode="navigation"
                  createOrganizationUrl="#create-org"
                  afterSelectOrganizationUrl="/"
                  hidePersonal
                />

              </div>

              {/* User menu */}
              <div className="flex items-center space-x-4">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName || "User"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.emailAddresses[0]?.emailAddress ||
                      "user@example.com"}
                  </p>
                </div>
                <UserButton
                  afterSignOutUrl={process.env.REACT_APP_LANDING_URL || "https://uttik-landing-page-prod-325801373008.us-central1.run.app"}
                  userProfileProps={{
                    appearance: {
                      elements: {
                        navbarButton: {
                          "&.cl-navbarButton__billing": {              //disabled the billing menu button
                            display: "none !important",
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </header>

        {/* Create Org Modal */}
        {createOrgOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black opacity-50"
              onClick={closeCreateOrg}
            />
            <div className="relative bg-white rounded-lg shadow-lg p-6 max-w-md w-full z-10">
              <button
                className="absolute top-3 right-3"
                onClick={closeCreateOrg}
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>{" "}              <CustomCreateOrganization
                isModal={false}
                onSuccess={() => {
                  // console.log("Organization created successfully:", org);
                  closeCreateOrg();
                  // Just refresh the component state, no reload needed
                  setRefreshKey((prev) => prev + 1);
                }}
              />
            </div>
          </div>
        )}

        {/* Main area */}
        <main className="flex-1 bg-gray-50 relative">
          {isSuspended ? (
            <div className="w-full min-h-[calc(100vh-4rem)] z-40 bg-gray-50/80 backdrop-blur-sm flex flex-col items-center justify-center px-6 pb-20">
              <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-red-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-red-500"></div>
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Access Suspended</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  You are suspended by admin. Please connect to support for more information.
                </p>
                <a
                  href="https://uttik.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-black text-white font-medium rounded-xl hover:bg-gray-800 transition-colors w-full"
                >
                  Contact Support
                </a>
              </div>
            </div>
          ) : (
            <div className={`p-1.5 sm:p-3 md:p-4 mx-auto${location.pathname === "/support-inbox" ? "" : " max-w-7xl"}`}>
              {" "}
              <Routes>
                <Route path="/" element={<Overview key={refreshKey} />} />
                <Route
                  path="/about"
                  element={<AboutSection key={refreshKey} />}
                />
                <Route
                  path="/knowledge"
                  element={<KnowledgeVault key={refreshKey} />}
                />
                <Route
                  path="/knowledge/:id"
                  element={<KnowledgeVault key={refreshKey} />}
                />
                <Route
                  path="/faqs"
                  element={<FAQManagement key={refreshKey} />}
                />
                <Route
                  path="/widgets"
                  element={<WidgetManager key={refreshKey} />}
                />
                <Route
                  path="/optimization"
                  element={<AnswerOptimization key={refreshKey} />}
                />
                <Route
                  path="/support"
                  element={<SupportQueries key={refreshKey} />}
                />
                <Route
                  path="/support/:ticket_id"
                  element={<SupportQueries key={refreshKey} />}
                />
                <Route
                  path="/support-inbox"
                  element={<SupportInbox key={refreshKey} />}
                />
                <Route
                  path="/conversations"
                  element={<ConversationsPage key={refreshKey} />}
                />
                <Route
                  path="/api-keys"
                  element={<ApiKeys key={refreshKey} />}
                />
                <Route
                  path="/api-guide"
                  element={<ApiGuide key={refreshKey} />}
                />
                <Route
                  path="/analytics"
                  element={<AITrafficAnalytics key={refreshKey} isMenuOpen={sidebarOpen} />}
                />
                <Route
                  path="/csat"
                  element={<CSATScores key={refreshKey} />}
                />
                <Route
                  path="/reports"
                  element={<Reports key={refreshKey} isMenuOpen={sidebarOpen} />}
                />
                <Route path="/billing" element={<Billing key={refreshKey} />} />
                <Route path="/invoices" element={<Invoices key={refreshKey} />} />
                <Route path="/integrations" element={<IntegrationsLanding key={refreshKey} />} />
                <Route path="/integrations/instagram" element={<Integrations key={refreshKey} />} />
                <Route path="/integrations/:id" element={<IntegrationDetails key={refreshKey} />} />
                <Route path="/integrations/instagram/:id" element={<IntegrationDetails key={refreshKey} />} />
                <Route path="/integrations/woocommerce" element={<WooCommerceIntegrations key={refreshKey} />} />
                <Route path="/integrations/woocommerce/:id" element={<WooCommerceIntegrationDetails key={refreshKey} />} />
              </Routes>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ setSidebarOpen, isSuspended }) {
  const location = useLocation();
  const { organization } = useOrganization();
  const { getCurrentRole, isAdmin } = useOrganizationContext();
  const { billingInfo } = useBillingStore();
  console.log('here is the organization', organization);

  return (
    <div className="relative h-full flex flex-col">
      {isSuspended && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-50 pointer-events-auto cursor-not-allowed" />
      )}
      {/* Branding */}
      <div className="flex items-center px-6 py-5 border-b border-gray-200">
        <div className="flex items-center space-x-3 w-full">
          <img
            src="/logo.svg"
            alt="Uttik"
            className="h-10 w-10 md:h-12 md:w-12 object-contain rounded"
          />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Uttik</h1>
            <p className="text-xs text-gray-500 font-medium">AI Answer Engine</p>
          </div>
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-4 py-6 space-y-1">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                ? "bg-gray-100 text-black border-r-2 border-black"
                : "text-gray-700 hover:bg-gray-50 hover:text-black"
                }`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon
                className={`mr-3 h-5 w-5 transition-colors ${isActive
                  ? "text-black"
                  : "text-gray-400 group-hover:text-gray-600"
                  }`}
              />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Org stats */}
      {organization && (
        <div className="px-4 py-4 border-t border-gray-200 bg-gray-50/50">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-black" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-black truncate">
                  {organization.name}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {isAdmin() ? "Administrator" : "Member"} •{" "}
                  {getCurrentRole().charAt(0).toUpperCase() +
                    getCurrentRole().slice(1)}
                </p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 min-w-0">
                    <Users className="h-3 w-3 text-gray-600 flex-shrink-0" />
                    <span className="text-xs text-gray-600">
                      {organization.membersCount || 1} member
                      {(organization.membersCount || 1) !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

              </div>


            </div>
            <div className="mt-2">
              <UsageCard title="Credits" used={billingInfo?.usage?.tokens || 0} limit={billingInfo?.limits?.tokens || 0} Icon={CreditCard} />
            </div>

            <div className="mt-2 flex items-center gap-2 bg-white/60 rounded px-2 py-1.5 border border-gray-200">
              <span className="text-xs text-gray-600 font-mono truncate flex-1">
                ID: {organization.id}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(organization.id);
                  // Show toast message - you'll need to implement your toast function
                  // Example: toast.success("Organization ID copied!");
                  // Or if using react-hot-toast: toast.success("Organization ID copied!");
                  toast.success("Organization ID copied!")
                }}
                className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                title="Copy Organization ID"
              >
                <Copy className="h-3 w-3 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
